// Birinci taraf sayfa ziyaret takibi (admin "Analiz" ekranı için).
// - Tüketici sayfaları ('web'): yalnızca çerez tercihlerinde analitik izni varsa.
// - /crm ('crm-web'): CRM oturumu (crmToken) varsa — B2B iş aracı, personel kullanımı.
// Olaylar bellekte biriktirilir; 15 sn'de bir, rota değişince ve sayfa gizlenince/kapanınca
// toplu gönderilir (sunucuda IP başına 180 istek/dk sınırı var). Hiçbir zaman hata fırlatmaz,
// toast göstermez. IP/tam user-agent tutulmaz; yol (path) sorgu dizesi olmadan gönderilir.
import { API_URL, API_KEY } from '../config/env';
import { decrypt } from '../utils/crypto';
import { CONSENT_EVENT, COOKIE_PREFS_KEY, hasAnalyticsConsent } from './consent';

const TRACK_URL = `${API_URL}Analytics/track`;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 15 * 1000;
const PING_INTERVAL_MS = 2 * 60 * 1000;
const ROUTE_FLUSH_DELAY_MS = 2000;
const TITLE_DELAY_MS = 1200;
const MAX_BATCH = 50;
const MAX_QUEUE = 300;
const MAX_RETRIES = 3;

const STORAGE = {
  web: { visitor: 'odz_vid', session: 'odz_sid', token: 'authToken' },
  'crm-web': { visitor: 'odz_crm_vid', session: 'odz_crm_sid', token: 'crmToken' },
};

let initialized = false;
/** { id, path, platform, sessionId, visibleMs, visibleSince } */
let current = null;
/** { platform, event, retries } */
let queue = [];
let flushTimer = null;
let hiddenAt = null;
let referrerUsed = false;
const memoryStore = {};

const now = () => Date.now();
const iso = (ms) => new Date(ms ?? now()).toISOString();

const uuid = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
};

const storageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return memoryStore[key] ?? null;
  }
};

const storageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    memoryStore[key] = value;
  }
};

const storageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // yok say
  }
  delete memoryStore[key];
};

// /crm-sozlesmesi gibi site sayfaları CRM sayılmasın: yalnızca /crm ve /crm/...
const platformFor = (pathname) => (/^\/crm(\/|$)/.test(pathname || '') ? 'crm-web' : 'web');

const normalizePath = (pathname) => {
  let p = (pathname || '/').split(/[?#]/)[0] || '/';
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  // Kiracı davet kodu bir erişim anahtarıdır (14 gün geçerli): analitiğe yazılmaz
  p = p.replace(/^\/kiraci\/davet\/[^/]+/i, '/kiraci/davet/:kod');
  return p.slice(0, 300);
};

const getToken = (platform) => {
  try {
    const encrypted = storageGet(STORAGE[platform].token);
    if (!encrypted) return null;
    const token = decrypt(encrypted);
    return typeof token === 'string' && token.length > 20 ? token : null;
  } catch {
    return null;
  }
};

const canTrack = (platform) => (platform === 'crm-web' ? !!getToken('crm-web') : hasAnalyticsConsent());

const getVisitorId = (platform) => {
  const key = STORAGE[platform].visitor;
  let id = storageGet(key);
  if (!id || id.length > 64) {
    id = uuid();
    storageSet(key, id);
  }
  return id;
};

const readSession = (platform) => {
  try {
    const s = JSON.parse(storageGet(STORAGE[platform].session) || 'null');
    return s && typeof s.id === 'string' ? s : null;
  } catch {
    return null;
  }
};

const sessionExpired = (platform) => {
  const s = readSession(platform);
  return !s || now() - (Number(s.last) || 0) > SESSION_TIMEOUT_MS;
};

/** Oturumu döndürür; 30 dk hareketsizlikten sonra yenisini başlatır. Son etkinliği günceller. */
const touchSession = (platform) => {
  let s = readSession(platform);
  let isNew = false;
  if (!s || now() - (Number(s.last) || 0) > SESSION_TIMEOUT_MS) {
    s = { id: uuid() };
    isNew = true;
  }
  storageSet(STORAGE[platform].session, JSON.stringify({ id: s.id, last: now() }));
  return { id: s.id, isNew };
};

const externalReferrer = () => {
  try {
    if (referrerUsed || !document.referrer) return null;
    referrerUsed = true;
    const u = new URL(document.referrer);
    if (u.origin === window.location.origin) return null;
    return `${u.origin}${u.pathname}`.slice(0, 300);
  } catch {
    return null;
  }
};

const visibleSeconds = (v) =>
  Math.max(0, Math.round((v.visibleMs + (v.visibleSince ? now() - v.visibleSince : 0)) / 1000));

const enqueue = (platform, event) => {
  queue.push({ platform, event, retries: 0 });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
};

const enqueuePing = (v) => {
  // Aynı görüntüleme için bekleyen eski ping'i at; yalnızca en güncel süre gitsin
  queue = queue.filter((q) => !(q.event.type === 'ping' && q.event.id === v.id));
  enqueue(v.platform, { type: 'ping', id: v.id, at: iso(), durationSeconds: visibleSeconds(v) });
};

const startView = (path, platform) => {
  const session = touchSession(platform);
  const view = {
    id: uuid(),
    path,
    platform,
    sessionId: session.id,
    visibleMs: 0,
    visibleSince: document.visibilityState === 'visible' ? now() : null,
  };
  const event = { type: 'view', id: view.id, sessionId: session.id, path, at: iso() };
  if (session.isNew) {
    const ref = externalReferrer();
    if (ref) event.referrer = ref;
  }
  enqueue(platform, event);
  current = view;

  // Başlık (Helmet) render'dan sonra güncellenir; kısa gecikmeyle oku
  setTimeout(() => {
    try {
      if (current && current.id === view.id && !event.title && document.title) {
        event.title = document.title.slice(0, 200);
      }
    } catch {
      // yok say
    }
  }, TITLE_DELAY_MS);
};

const endCurrentView = (atMs) => {
  if (!current) return;
  const v = current;
  current = null;
  enqueue(v.platform, { type: 'leave', id: v.id, at: iso(atMs), durationSeconds: visibleSeconds(v) });
};

const requeue = (platform, events, retries) => {
  if (retries >= MAX_RETRIES) return;
  const items = events.map((event) => ({ platform, event, retries: retries + 1 }));
  queue = items.concat(queue).slice(-MAX_QUEUE);
};

const send = (platform, events, keepalive, retries) => {
  try {
    const headers = { 'Content-Type': 'application/json', 'x-root': API_KEY };
    const token = getToken(platform);
    if (token) headers.Authorization = `Bearer ${token}`;
    const body = JSON.stringify({
      visitorId: getVisitorId(platform),
      sessionId: events.find((e) => e.sessionId)?.sessionId || readSession(platform)?.id || null,
      platform,
      sentAt: iso(),
      events,
    });
    // keepalive yalnızca sayfa gizlenirken/kapanırken: normal gönderimde gerek yok (64KB kota paylaşımlı)
    fetch(TRACK_URL, { method: 'POST', headers, body, keepalive: !!keepalive, credentials: 'omit' })
      .then((res) => {
        if (res.status === 429 || res.status >= 500) requeue(platform, events, retries);
      })
      .catch(() => requeue(platform, events, retries));
  } catch {
    // takip asla uygulamayı bozmamalı
  }
};

export const flush = (keepalive = false) => {
  try {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!queue.length) return;
    const items = queue;
    queue = [];

    const groups = {};
    items.forEach((it) => {
      const key = `${it.platform}|${it.retries}`;
      if (!groups[key]) groups[key] = { platform: it.platform, retries: it.retries, events: [] };
      groups[key].events.push(it.event);
    });

    Object.values(groups).forEach(({ platform, retries, events }) => {
      // Kuyruktayken izin geri alındıysa tüketici olaylarını gönderme
      if (platform === 'web' && !hasAnalyticsConsent()) return;
      for (let i = 0; i < events.length; i += MAX_BATCH) {
        send(platform, events.slice(i, i + MAX_BATCH), keepalive, retries);
      }
    });
  } catch {
    // yok say
  }
};

const scheduleFlush = (delay) => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush(false);
  }, delay);
};

const onVisibilityChange = () => {
  try {
    if (document.visibilityState === 'hidden') {
      if (current) {
        if (current.visibleSince) {
          current.visibleMs += now() - current.visibleSince;
          current.visibleSince = null;
        }
        hiddenAt = now();
        enqueuePing(current);
      }
      // Mobil tarayıcılarda bu, sayfa kapanmadan önceki son güvenilir an olabilir
      flush(true);
      return;
    }

    if (!current) return;
    if (sessionExpired(current.platform)) {
      // 30 dk'dan uzun gizli kaldı: eski görüntülemeyi gizlendiği anda kapat, yeni oturumla devam et
      const { path, platform } = current;
      endCurrentView(hiddenAt ?? undefined);
      if (canTrack(platform)) startView(path, platform);
      scheduleFlush(ROUTE_FLUSH_DELAY_MS);
    } else {
      current.visibleSince = now();
      touchSession(current.platform);
    }
  } catch {
    // yok say
  }
};

const onPageHide = () => {
  try {
    endCurrentView();
    flush(true);
  } catch {
    // yok say
  }
};

const onPageShow = (e) => {
  // bfcache'ten geri dönüş: aynı sayfa için yeni görüntüleme başlat
  if (e && e.persisted) trackPageView(window.location.pathname);
};

const pingTick = () => {
  try {
    if (!current || document.visibilityState !== 'visible') return;
    if (!canTrack(current.platform)) return;
    touchSession(current.platform);
    enqueuePing(current);
  } catch {
    // yok say
  }
};

const onConsentChanged = () => {
  try {
    if (hasAnalyticsConsent()) {
      if (!current && platformFor(window.location.pathname) === 'web') {
        trackPageView(window.location.pathname);
      }
      return;
    }
    // İzin geri alındı/verilmedi: tüketici tarafı kuyruğunu ve tanımlayıcıları sil
    queue = queue.filter((q) => q.platform !== 'web');
    if (current && current.platform === 'web') current = null;
    storageRemove(STORAGE.web.visitor);
    storageRemove(STORAGE.web.session);
  } catch {
    // yok say
  }
};

const onStorage = (e) => {
  if (e && e.key === COOKIE_PREFS_KEY) onConsentChanged();
};

const init = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener(CONSENT_EVENT, onConsentChanged);
  window.addEventListener('storage', onStorage);
  setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  setInterval(pingTick, PING_INTERVAL_MS);
  // İzin yoksa daha önce oluşmuş tüketici tanımlayıcılarını temizle
  if (!hasAnalyticsConsent()) {
    storageRemove(STORAGE.web.visitor);
    storageRemove(STORAGE.web.session);
  }
};

/** Rota değişiminde çağrılır: önceki görüntülemeyi kapatır, izin varsa yenisini başlatır. */
export function trackPageView(pathname) {
  try {
    init();
    const platform = platformFor(pathname);
    const path = normalizePath(pathname);
    if (current && current.path === path && current.platform === platform) return;
    endCurrentView();
    if (canTrack(platform)) startView(path, platform);
    scheduleFlush(ROUTE_FLUSH_DELAY_MS);
  } catch {
    // takip asla uygulamayı bozmamalı
  }
}
